// Dedicated milestones page (extracted from /growth). Big reel + categorised
// log + own time-spaced timeline.

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { HeaderBar, HeaderIconButton } from '../components/HeaderBar.tsx';
import { Icon } from '../components/Icon.tsx';
import { useMe } from '../api/hooks.ts';
import {
  useCreateMilestone,
  useDeleteMilestone,
  useMilestones,
  useUpdateMilestone,
} from '../api/milestones.ts';
import { MilestoneSheet } from '../components/sheets/MilestoneSheet.tsx';
import { TimelineEntry, TimelineSpine, type SpineItem } from '../components/growth/TimelineSpine.tsx';
import { fmtAge, ageDaysAt } from '../lib/measure.ts';
import { MILESTONE_BY_SLUG } from '@shared/milestones.ts';
import type { Milestone } from '@shared/types.ts';
import { fmtTime, relAgo } from '../lib/format.ts';

export function Milestones() {
  const { data: me } = useMe();
  const baby = me?.babies.find((b) => b.id === me?.active_baby_id) ?? me?.babies[0] ?? null;
  const { data: milestones = [] } = useMilestones(baby?.id);

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Milestone | null>(null);

  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get('add') === '1') {
      setAdding(true);
      const next = new URLSearchParams(params);
      next.delete('add');
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = useCreateMilestone();
  const update = useUpdateMilestone();
  const del = useDeleteMilestone();

  const usedSlugs = useMemo(
    () => new Set(milestones.map((m) => m.kind).filter((k) => k !== 'custom')),
    [milestones],
  );

  const spineItems = useMemo<SpineItem[]>(
    () =>
      milestones.map((ms) => ({
        key: ms.id,
        at: ms.occurred_at,
        accent: 'var(--accent)',
        render: () => (
          <MilestoneRow ms={ms} birthDate={baby?.birth_date ?? null} onClick={() => setEditing(ms)} />
        ),
      })),
    [milestones, baby?.birth_date],
  );

  return (
    <>
      <HeaderBar
        baby={baby}
        title="Milestones"
        subtitle={baby ? `${baby.name} · ${fmtAge(ageDaysAt(baby.birth_date, Math.floor(Date.now() / 1000)))}` : null}
        rightSlot={
          <HeaderIconButton ariaLabel="Log milestone" onClick={() => setAdding(true)}>
            <Icon.plus s={18} />
          </HeaderIconButton>
        }
      />
      <div className="lt-scroll" style={{ flex: 1, padding: '0 16px 24px' }}>
        {milestones.length === 0 ? (
          <div
            style={{
              padding: '40px 22px',
              borderRadius: 22,
              background: 'var(--accent-soft)',
              border: '1px solid var(--accent-line)',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 38 }}>✨</div>
            <div
              className="lt-serif"
              style={{ fontSize: 22, fontWeight: 500, marginTop: 10, color: 'var(--text-1)' }}
            >
              Log the firsts
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 6, lineHeight: 1.4 }}>
              First smile, first crawl, first word — capture the moments worth remembering.
            </div>
            <button
              type="button"
              className="lt-btn lt-btn-primary"
              style={{ padding: '11px 18px', fontSize: 14, marginTop: 16 }}
              onClick={() => setAdding(true)}
            >
              Log a milestone
            </button>
          </div>
        ) : (
          <>
            {/* Reel of recent milestone chips */}
            <div
              className="lt-scroll"
              style={{
                display: 'flex',
                gap: 10,
                overflowX: 'auto',
                padding: '4px 4px 8px',
                margin: '0 -4px 12px',
                maskImage:
                  'linear-gradient(to right, black calc(100% - 24px), transparent)',
              }}
            >
              {milestones
                .slice()
                .sort((a, b) => b.occurred_at - a.occurred_at)
                .map((ms) => (
                  <MilestoneChip
                    key={ms.id}
                    ms={ms}
                    birthDate={baby?.birth_date ?? null}
                    onClick={() => setEditing(ms)}
                  />
                ))}
            </div>

            <TimelineSpine items={spineItems} scale="days" />
          </>
        )}
      </div>

      <MilestoneSheet
        open={adding}
        usedSlugs={usedSlugs}
        onClose={() => setAdding(false)}
        onSubmit={async (vars) => {
          try {
            await create.mutateAsync(vars);
            toast.success(`${vars.title} logged 🎉`);
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Save failed');
          }
        }}
      />
      <MilestoneSheet
        open={!!editing}
        existing={editing}
        usedSlugs={usedSlugs}
        onClose={() => setEditing(null)}
        onSubmit={async (vars) => {
          if (!editing) return;
          try {
            await update.mutateAsync({
              id: editing.id,
              patch: {
                kind: vars.kind,
                title: vars.title,
                occurred_at: vars.occurred_at,
                notes: vars.notes,
              },
            });
            toast.success('Saved');
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Save failed');
          }
        }}
        onDelete={async () => {
          if (!editing) return;
          try {
            await del.mutateAsync(editing.id);
            toast.success('Deleted');
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Delete failed');
          }
        }}
      />
    </>
  );
}

function MilestoneChip({
  ms,
  birthDate,
  onClick,
}: {
  ms: Milestone;
  birthDate: string | null;
  onClick: () => void;
}) {
  const preset = ms.kind !== 'custom' ? MILESTONE_BY_SLUG.get(ms.kind) : null;
  const emoji = preset?.emoji ?? '⭐';
  const age = birthDate ? fmtAge(ageDaysAt(birthDate, ms.occurred_at)) : '';
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flexShrink: 0,
        width: 140,
        padding: '14px 12px',
        borderRadius: 18,
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <div style={{ fontSize: 28, lineHeight: 1 }}>{emoji}</div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--text-1)',
          letterSpacing: -0.005,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {ms.title}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
        {age && `${age} · `}
        {relAgo(ms.occurred_at)}
      </div>
    </button>
  );
}

function MilestoneRow({
  ms,
  birthDate,
  onClick,
}: {
  ms: Milestone;
  birthDate: string | null;
  onClick: () => void;
}) {
  const preset = ms.kind !== 'custom' ? MILESTONE_BY_SLUG.get(ms.kind) : null;
  const emoji = preset?.emoji ?? '⭐';
  const age = birthDate ? fmtAge(ageDaysAt(birthDate, ms.occurred_at)) : null;
  const d = new Date(ms.occurred_at * 1000);
  return (
    <TimelineEntry
      onClick={onClick}
      hasNotes={!!ms.notes}
      leading={<span style={{ fontSize: 18, lineHeight: 1 }}>{emoji}</span>}
      title={ms.title}
      subtitle={
        <>
          {age && `${age} · `}
          {d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} ·{' '}
          {relAgo(ms.occurred_at)}
        </>
      }
    />
  );
}
